/**
 * HR GAMIFICATION SYSTEM CONSTANTS
 *
 * This file defines all the gamification rules, achievements, levels, and point calculations
 * for HR/Recruiter users on the VetriaAI platform.
 */

// ============ HR LEVEL SYSTEM ============

export const HR_LEVELS = [
  {
    level: 1,
    name: 'Junior Recruiter',
    emoji: '🌱',
    minXP: 0,
    maxXP: 200,
    color: 'from-green-400 to-green-600',
    description: 'Starting your recruiting journey',
    jobsRequired: 0
  },
  {
    level: 2,
    name: 'Active Recruiter',
    emoji: '⚡',
    minXP: 200,
    maxXP: 500,
    color: 'from-blue-400 to-blue-600',
    description: 'Building your talent pipeline',
    jobsRequired: 5
  },
  {
    level: 3,
    name: 'Senior Recruiter',
    emoji: '🔥',
    minXP: 500,
    maxXP: 1200,
    color: 'from-orange-400 to-red-600',
    description: 'Experienced in hiring',
    jobsRequired: 15
  },
  {
    level: 4,
    name: 'Hiring Manager',
    emoji: '💎',
    minXP: 1200,
    maxXP: 2500,
    color: 'from-purple-400 to-purple-600',
    description: 'Master of talent acquisition',
    jobsRequired: 30
  },
  {
    level: 5,
    name: 'Talent Leader',
    emoji: '👑',
    minXP: 2500,
    maxXP: 5000,
    color: 'from-yellow-400 to-yellow-600',
    description: 'Elite recruiter status',
    jobsRequired: 50
  },
  {
    level: 6,
    name: 'Talent Leader',
    emoji: '👑',
    minXP: 5000,
    maxXP: 10000,
    color: 'from-yellow-400 to-yellow-600',
    description: 'Legendary recruiter',
    jobsRequired: 75
  }
];

// ============ XP POINTS SYSTEM ============

export const HR_XP_POINTS = {
  // Job Posting Activities
  POST_JOB: 20,              // Create a new job posting
  UPDATE_JOB: 5,             // Update an existing job
  CLOSE_JOB: 10,             // Successfully close a job posting

  // Test Creation Activities
  CREATE_TEST: 30,           // Create a new assessment test
  PUBLISH_TEST: 15,          // Publish a test
  UPDATE_TEST: 5,            // Modify an existing test

  // Candidate Management Activities
  REVIEW_CANDIDATE: 5,       // View a candidate's profile/results
  SHORTLIST_CANDIDATE: 10,   // Shortlist a candidate
  REJECT_CANDIDATE: 5,       // Reject a candidate (with feedback)
  INTERVIEW_CANDIDATE: 15,   // Schedule/complete an interview

  // Hiring Activities
  MAKE_OFFER: 50,            // Make a job offer
  SUCCESSFUL_HIRE: 100,      // Candidate accepts and joins

  // Engagement Activities
  VIEW_DASHBOARD: 2,         // Daily login bonus
  VIEW_TEST_RESULTS: 5,      // Review test results
  DOWNLOAD_REPORT: 5,        // Download candidate reports

  // Streak Bonuses
  STREAK_7_DAY: 25,          // Maintain 7-day streak
  STREAK_14_DAY: 50,         // Maintain 14-day streak
  STREAK_30_DAY: 100,        // Maintain 30-day streak
  STREAK_60_DAY: 200,        // Maintain 60-day streak

  // Quality Bonuses
  FAST_RESPONSE: 10,         // Respond to candidate within 24 hours
  HIGH_QUALITY_JD: 15,       // Well-written job description
  COMPLETE_PROFILE: 20,      // Fill out complete company profile
};

// ============ HR ACHIEVEMENTS ============

export interface HRAchievement {
  id: string;
  title: string;
  description: string;
  emoji: string;
  points: number;
  category: 'posting' | 'testing' | 'hiring' | 'engagement' | 'milestone' | 'quality';
  requirement: number;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
}

export const HR_ACHIEVEMENTS: HRAchievement[] = [
  // ===== POSTING ACHIEVEMENTS =====
  {
    id: 'first_job',
    title: 'First Posting',
    description: 'Created your first job posting',
    emoji: '🎯',
    points: 10,
    category: 'posting',
    requirement: 1,
    tier: 'bronze'
  },
  {
    id: 'job_poster_5',
    title: 'Active Poster',
    description: 'Posted 5 job openings',
    emoji: '📋',
    points: 25,
    category: 'posting',
    requirement: 5,
    tier: 'bronze'
  },
  {
    id: 'job_poster_10',
    title: 'Hiring Machine',
    description: 'Posted 10 job openings',
    emoji: '🏭',
    points: 50,
    category: 'posting',
    requirement: 10,
    tier: 'silver'
  },
  {
    id: 'job_poster_25',
    title: 'Mass Recruiter',
    description: 'Posted 25 job openings',
    emoji: '🚀',
    points: 100,
    category: 'posting',
    requirement: 25,
    tier: 'gold'
  },

  // ===== TESTING ACHIEVEMENTS =====
  {
    id: 'first_test',
    title: 'Test Creator',
    description: 'Created your first assessment test',
    emoji: '📝',
    points: 15,
    category: 'testing',
    requirement: 1,
    tier: 'bronze'
  },
  {
    id: 'test_master_10',
    title: 'Assessment Expert',
    description: 'Created 10 unique tests',
    emoji: '🎓',
    points: 75,
    category: 'testing',
    requirement: 10,
    tier: 'silver'
  },
  {
    id: 'test_master_25',
    title: 'Testing Guru',
    description: 'Created 25 comprehensive tests',
    emoji: '🧠',
    points: 150,
    category: 'testing',
    requirement: 25,
    tier: 'gold'
  },

  // ===== CANDIDATE REVIEW ACHIEVEMENTS =====
  {
    id: 'reviewer_10',
    title: 'Eagle Eye',
    description: 'Reviewed 10 candidates',
    emoji: '👀',
    points: 20,
    category: 'hiring',
    requirement: 10,
    tier: 'bronze'
  },
  {
    id: 'reviewer_50',
    title: 'Talent Scout',
    description: 'Reviewed 50 candidates',
    emoji: '🔍',
    points: 50,
    category: 'hiring',
    requirement: 50,
    tier: 'silver'
  },
  {
    id: 'reviewer_100',
    title: 'Talent Hunter',
    description: 'Reviewed 100 candidates',
    emoji: '🎯',
    points: 100,
    category: 'hiring',
    requirement: 100,
    tier: 'gold'
  },

  // ===== HIRING ACHIEVEMENTS =====
  {
    id: 'first_hire',
    title: 'First Hire',
    description: 'Made your first successful hire',
    emoji: '🤝',
    points: 50,
    category: 'hiring',
    requirement: 1,
    tier: 'silver'
  },
  {
    id: 'hire_5',
    title: 'Team Builder',
    description: 'Successfully hired 5 candidates',
    emoji: '👥',
    points: 100,
    category: 'hiring',
    requirement: 5,
    tier: 'gold'
  },
  {
    id: 'hire_10',
    title: 'Talent Magnet',
    description: 'Successfully hired 10 candidates',
    emoji: '🧲',
    points: 200,
    category: 'hiring',
    requirement: 10,
    tier: 'platinum'
  },

  // ===== SPEED ACHIEVEMENTS =====
  {
    id: 'speed_demon',
    title: 'Speed Demon',
    description: 'Responded to 10 candidates within 24 hours',
    emoji: '⚡',
    points: 50,
    category: 'quality',
    requirement: 10,
    tier: 'silver'
  },
  {
    id: 'lightning_fast',
    title: 'Lightning Fast',
    description: 'Responded to 25 candidates within 24 hours',
    emoji: '⚡⚡',
    points: 100,
    category: 'quality',
    requirement: 25,
    tier: 'gold'
  },

  // ===== STREAK ACHIEVEMENTS =====
  {
    id: 'streak_7',
    title: 'Weekly Warrior',
    description: 'Maintained a 7-day activity streak',
    emoji: '🔥',
    points: 30,
    category: 'engagement',
    requirement: 7,
    tier: 'bronze'
  },
  {
    id: 'streak_14',
    title: 'Fortnight Champion',
    description: 'Maintained a 14-day activity streak',
    emoji: '🔥🔥',
    points: 60,
    category: 'engagement',
    requirement: 14,
    tier: 'silver'
  },
  {
    id: 'streak_30',
    title: 'Monthly Master',
    description: 'Maintained a 30-day activity streak',
    emoji: '🔥🔥🔥',
    points: 120,
    category: 'engagement',
    requirement: 30,
    tier: 'gold'
  },
  {
    id: 'streak_60',
    title: 'Consistency King',
    description: 'Maintained a 60-day activity streak',
    emoji: '👑',
    points: 250,
    category: 'engagement',
    requirement: 60,
    tier: 'platinum'
  },

  // ===== MILESTONE ACHIEVEMENTS =====
  {
    id: 'power_user',
    title: 'Power User',
    description: 'Reached Level 3',
    emoji: '💪',
    points: 50,
    category: 'milestone',
    requirement: 3,
    tier: 'silver'
  },
  {
    id: 'elite_recruiter',
    title: 'Elite Recruiter',
    description: 'Reached Level 5',
    emoji: '⭐',
    points: 150,
    category: 'milestone',
    requirement: 5,
    tier: 'platinum'
  },

  // ===== QUALITY ACHIEVEMENTS =====
  {
    id: 'quality_recruiter',
    title: 'Quality Recruiter',
    description: 'Achieved 90% candidate satisfaction',
    emoji: '🌟',
    points: 100,
    category: 'quality',
    requirement: 90,
    tier: 'gold'
  },
  {
    id: 'detail_oriented',
    title: 'Detail Oriented',
    description: 'Completed profile with all information',
    emoji: '✅',
    points: 25,
    category: 'quality',
    requirement: 100,
    tier: 'bronze'
  }
];

// ============ HELPER FUNCTIONS ============

export const getHRLevelByXP = (xp: number) => {
  for (let i = HR_LEVELS.length - 1; i >= 0; i--) {
    if (xp >= HR_LEVELS[i].minXP) {
      return HR_LEVELS[i];
    }
  }
  return HR_LEVELS[0];
};

export const calculateHRLevelProgress = (xp: number) => {
  const currentLevel = getHRLevelByXP(xp);
  const nextLevel = HR_LEVELS.find(l => l.level === currentLevel.level + 1);

  if (!nextLevel) {
    return { percentage: 100, xpInLevel: 0, xpNeeded: 0 };
  }

  const xpInLevel = xp - currentLevel.minXP;
  const xpNeeded = nextLevel.minXP - currentLevel.minXP;
  const percentage = Math.min((xpInLevel / xpNeeded) * 100, 100);

  return { percentage, xpInLevel, xpNeeded };
};

export const getAchievementsByCategory = (category: HRAchievement['category']) => {
  return HR_ACHIEVEMENTS.filter(a => a.category === category);
};

export const getAchievementsByTier = (tier: HRAchievement['tier']) => {
  return HR_ACHIEVEMENTS.filter(a => a.tier === tier);
};

